import { useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { useEmailRealtimeManager, EmailInsertEvent } from './useEmailRealtimeManager';

export function useAutoTaskSuggestions() {
  const queryClient = useQueryClient();
  const processingRef = useRef(new Set<string>());

  useEmailRealtimeManager(async (newEmail: EmailInsertEvent) => {
    // Prevent duplicate processing
    if (processingRef.current.has(newEmail.id)) return;
    processingRef.current.add(newEmail.id);

    console.log('🔍 New email received, extracting task suggestions:', newEmail.subject);

    try {
      // Auto-extract suggestions in background
      const { data, error } = await supabase.functions.invoke('extract-email-tasks', {
        body: {
          emailIds: [newEmail.id],
          threadId: newEmail.thread_id,
          previewMode: true,
        },
      });

      if (error) throw error;

      if (data?.suggestionsCreated > 0) {
        // Invalidate suggestions query to show new suggestions
        queryClient.invalidateQueries({ queryKey: ['task-suggestions'] });

        // Show non-intrusive notification
        toast({
          title: '✨ Task suggestions ready',
          description: `${data.suggestionsCreated} action item${data.suggestionsCreated > 1 ? 's' : ''} found in "${newEmail.subject}". Click to review.`,
          duration: 8000,
        });
        
        // Auto-open dialog after a short delay
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-task-suggestions'));
        }, 1000);
      }
    } catch (error) {
      console.error('Auto-extraction error:', error);
    } finally {
      processingRef.current.delete(newEmail.id);
    }
  });
}
